import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { ExperienceEventFragment } from '@/features/events/fragments/ExperienceEventFragment';
import { fetchAllExperienceEvents } from '@/features/events/lib/eventPages.lib';
import { gqlClient } from '@/lib';
import type {
  GetExperienceInput,
  ListExperienceEventInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceEventsQueryKey } from '@/utils/queryKeys';
import type { GetPageExperienceEventsQuery } from './useGetPageExperienceEvents.generated';

type RawEvent = GetPageExperienceEventsQuery['getExperience']['events'][number];

const query = gql`
  ${ExperienceEventFragment}
  query GetPageExperienceEvents(
    $input: GetExperienceInput!
    $filters: ListExperienceEventInput!
  ) {
    getExperience(input: $input) {
      events(filters: $filters) {
        ...ExperienceEvent
      }
    }
  }
`;

const selector = (data: GetPageExperienceEventsQuery): RawEvent[] => {
  return data.getExperience.events;
};

export const useGetPageExperienceEvents = (
  experienceId: string | undefined,
  filters: ListExperienceEventInput
) => {
  const queryKey = createExperienceEventsQueryKey(experienceId, filters);

  return useQuery<GetPageExperienceEventsQuery, QueryError, RawEvent[]>({
    enabled: Boolean(experienceId),
    queryKey,
    // The filters (and so the query key) include the planner's URL date range.
    // Keep the previous range's events while the new range loads so isPending
    // stays false across range changes — otherwise PlannerLayout swaps the
    // planner subtree for a full-page loader on every change, unmounting the
    // control bar (and closing the mobile "Jump to" drawer mid-interaction).
    placeholderData: keepPreviousData,
    select: selector,
    queryFn: async () => {
      const input: GetExperienceInput = {
        experienceId: experienceId!, // Covered by enabled above
      };
      const events = await fetchAllExperienceEvents(
        filters,
        async (pageFilters) => {
          const data = await gqlClient.request<GetPageExperienceEventsQuery>(
            query,
            { input, filters: pageFilters }
          );
          return selector(data);
        }
      );
      // Re-wrapped in the document's own shape so the cached value — and what
      // @normy normalizes out of it — is what an untruncated single response
      // would have been.
      return { getExperience: { events } };
    },
  });
};
